import { useMemo } from 'react'

import type { ToolbarPaymentListItem } from '#/shared/api/generated/shipment/models'
import { useListItemsApiV1ToolbarTypeGet } from '#/shared/api/generated/shipment/theCargoShipment'
import { formatNoteTimestamp, formatUsd } from '#/shared/lib/format'

const DEFAULT_PAYMENT_FEED_SIZE = 100

export interface ShipmentPaymentFeedItem {
  id: string
  amount: string
  customerName: string | undefined
  paymentType: string | undefined
  status: string | undefined
  direction: string | undefined
  reference: string | undefined
  cardLast4: string | undefined
  timestamp: string
  occurredAt: string
  isApp: boolean
}

interface UseShipmentPaymentFeedOptions {
  enabled?: boolean
  size?: number
}

export interface UseShipmentPaymentFeedResult {
  payments: ShipmentPaymentFeedItem[]
  isLoading: boolean
}

function parseAmount(raw: string): number | undefined {
  const value = Number(raw)
  return Number.isFinite(value) ? value : undefined
}

export function toShipmentPaymentFeedItem(
  item: ToolbarPaymentListItem,
): ShipmentPaymentFeedItem {
  return {
    id: item.id,
    amount: formatUsd(parseAmount(item.total_amount), { showCents: true }),
    customerName: item.customer_name ?? undefined,
    paymentType: item.payment_type ?? undefined,
    status: item.status ?? undefined,
    direction: item.direction ?? undefined,
    reference: item.reference ?? undefined,
    cardLast4: item.card_last4 ?? undefined,
    timestamp: item.created_at ? formatNoteTimestamp(item.created_at) : '',
    occurredAt: item.created_at ?? '',
    isApp: item.is_app ?? false,
  }
}

export function useShipmentPaymentFeed(
  shipmentId: string,
  {
    enabled = true,
    size = DEFAULT_PAYMENT_FEED_SIZE,
  }: UseShipmentPaymentFeedOptions = {},
): UseShipmentPaymentFeedResult {
  const query = useListItemsApiV1ToolbarTypeGet(
    'payment',
    { shipment_id: shipmentId, size },
    { query: { enabled: enabled && Boolean(shipmentId) } },
  )

  const payments = useMemo<ShipmentPaymentFeedItem[]>(() => {
    const items = (query.data?.data.items ??
      []) as unknown as ToolbarPaymentListItem[]
    return items.map(toShipmentPaymentFeedItem)
  }, [query.data])

  return {
    payments,
    isLoading: query.isLoading && enabled && Boolean(shipmentId),
  }
}
